/**
 * Entry Status (buy-readiness) code → display map.
 *
 * Single source of truth for the deep-analysis chart frontend slice
 * (StockChart `LevelsSummaryOverlay` pill + EntryPointDisplay header badge).
 * Backend (`core/entry_status.py`) emits the stable snake_case `entry_status`
 * code; this helper maps it to a label / Tailwind tone / emoji per
 * SPEC_ENTRY_STATUS_2026-06-18.md §3.
 *
 * Tailwind tokens are the project's semantic design-system colors (per
 * CLAUDE.md theming) - `accent-bullish` (green), `accent-bearish` (red),
 * `accent-warning` (amber). No raw hex.
 *
 * i18n: labels here are English defaults only - callers wrap them with
 * `t(..., { defaultValue })` so locale JSON can override later WITHOUT this
 * file (or any en/he/ar/ru.json) being edited now.
 */

export type EntryStatusCode =
  // Graded buy-conviction codes (SPEC_ENTRY_CONVICTION_2026-06-19 §1) - the
  // backend resolves the coarse buy-zone into one of these four.
  | 'strong_buy'
  | 'buy'
  | 'accumulate'
  | 'wait_for_reversal'
  // DEPRECATED alias (F-C2): the deep CHART still emits the coarse `in_buy_zone`
  // from its local `classifyEntryStatus`; keep it in the MAP so
  // `resolveEntryStatus('in_buy_zone')` never degrades to 'unknown'. Do NOT
  // remove until the chart ships graded codes via the StockData row (follow-up).
  | 'in_buy_zone'
  | 'holding'
  | 'extended'
  | 'below_stop'
  | 'unknown';

export interface EntryStatusDisplay {
  /** Stable code (normalized) - `unknown` for any unrecognized/missing input. */
  code: EntryStatusCode;
  /** English default label (callers pass through i18n `defaultValue`). */
  label: string;
  /** Leading emoji glyph ('' for unknown). */
  emoji: string;
  /**
   * Semantic Tailwind text-color class (theme-token backed, per CLAUDE.md
   * theming). Used for the chart pill text + the badge text.
   */
  textClass: string;
  /**
   * Semantic Tailwind background-tint + text class for the chart pill / badge
   * body. Uses the design-system muted tints (or an opacity modifier where no
   * dedicated muted token exists).
   */
  badgeClass: string;
}

export interface BuyZoneAdvice {
  low: number;
  high: number;
}

const BUY_ZONE_ADVICE_CODES: ReadonlySet<EntryStatusCode> = new Set<EntryStatusCode>([
  'accumulate',
  'wait_for_reversal',
  'in_buy_zone',
  'holding',
  'extended',
]);

const MAP: Record<EntryStatusCode, EntryStatusDisplay> = {
  // Graded buy-conviction codes (SPEC_ENTRY_CONVICTION_2026-06-19 §2 SSOT table
  // + F-L1 exact tokens). Tokens `accent-bullish`, `accent-bullish-muted`,
  // `accent-warning` confirmed present in design-system.css + tailwind.config.js.
  strong_buy: {
    code: 'strong_buy',
    label: 'Strong Buy',
    emoji: '🟢🟢',
    textClass: 'text-accent-bullish',
    badgeClass: 'bg-accent-bullish-muted text-accent-bullish font-bold',
  },
  buy: {
    code: 'buy',
    label: 'Buy',
    emoji: '🟢',
    textClass: 'text-accent-bullish',
    badgeClass: 'bg-accent-bullish-muted text-accent-bullish',
  },
  accumulate: {
    code: 'accumulate',
    label: 'Accumulate',
    emoji: '🟡🟢',
    textClass: 'text-accent-bullish',
    badgeClass: 'bg-accent-bullish/15 text-accent-bullish',
  },
  wait_for_reversal: {
    code: 'wait_for_reversal',
    label: 'Wait for Reversal',
    emoji: '⏳',
    textClass: 'text-accent-warning',
    badgeClass: 'bg-accent-warning/15 text-accent-warning',
  },
  // DEPRECATED alias (F-C2) - retained so the deep chart's coarse `in_buy_zone`
  // still resolves to a real badge instead of '-'.
  in_buy_zone: {
    code: 'in_buy_zone',
    label: 'In Buy Zone',
    emoji: '🟢',
    textClass: 'text-accent-bullish',
    badgeClass: 'bg-accent-bullish-muted text-accent-bullish',
  },
  holding: {
    code: 'holding',
    label: 'Holding',
    emoji: '🟡',
    textClass: 'text-accent-warning',
    badgeClass: 'bg-accent-warning/15 text-accent-warning',
  },
  extended: {
    code: 'extended',
    label: 'Extended',
    emoji: '🟠',
    textClass: 'text-accent-warning',
    badgeClass: 'bg-accent-warning/20 text-accent-warning',
  },
  below_stop: {
    code: 'below_stop',
    label: 'Below Stop',
    emoji: '🔴',
    textClass: 'text-accent-bearish',
    badgeClass: 'bg-accent-bearish-muted text-accent-bearish',
  },
  unknown: {
    code: 'unknown',
    label: '-',
    emoji: '',
    textClass: 'text-text-secondary',
    badgeClass: 'bg-surface-container text-text-secondary',
  },
};

/**
 * Pure buy-readiness classifier - mirrors the backend `classify_entry_status`
 * (SPEC_ENTRY_STATUS_2026-06-18.md §1) and the Pine watermark logic. Ordered
 * exactly per the spec table; degrades to 'unknown' on missing/out-of-order
 * anchors rather than throwing. No DB/network/AI.
 *
 * @param price       live/last price
 * @param entry       suggested entry
 * @param stop        stop-loss
 * @param target      first ladder target (T1); 0/undefined = "missing"
 * @param buyBandPct  buy-zone tolerance above entry (default 0.03 = 3%)
 */
export function classifyEntryStatus(
  price: number | null | undefined,
  entry: number | null | undefined,
  stop: number | null | undefined,
  target: number | null | undefined,
  buyBandPct: number = 0.03,
): EntryStatusCode {
  // Coerce like the Python `_to_float` (accepts numeric strings, rejects
  // NaN/inf/junk → 0) so TS and backend agree on parity-test inputs.
  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    return Number.isFinite(n) ? n : 0;
  };
  const p = num(price);
  const e = num(entry);
  const s = num(stop);
  const t = num(target);
  // Negative band is nonsensical - clamp to the default (mirrors Python).
  const band = Number.isFinite(buyBandPct) && buyBandPct >= 0 ? buyBandPct : 0.03;

  // price/entry invalid (<=0) → unknown
  if (p <= 0 || e <= 0) return 'unknown';
  // stop valid (>0) and price at/below it → below_stop. Checked BEFORE the
  // entry<=stop ordering guard: a live price at/below its stop is definitively
  // below_stop regardless of where the structural entry anchor sits (a weak
  // name under all its MAs anchors entry==price<=stop, which must not collapse
  // to a blank 'unknown'). Mirrors core/entry_status.py.
  if (s > 0 && p <= s) return 'below_stop';
  // entry<=stop AND price above the stop → genuinely ambiguous → unknown
  if (s > 0 && e <= s) return 'unknown';
  // at/just above entry → in_buy_zone
  if (p <= e * (1 + band)) return 'in_buy_zone';
  // target valid and price at/past it → extended
  if (t > 0 && p >= t) return 'extended';
  // otherwise (entry*(1+band) < price < target, or target missing) → holding
  return 'holding';
}

/** i18n key suffix per code (callers prefix with their namespace). */
export const ENTRY_STATUS_I18N_KEY: Record<EntryStatusCode, string> = {
  strong_buy: 'entryStatusStrongBuy',
  buy: 'entryStatusBuy',
  accumulate: 'entryStatusAccumulate',
  wait_for_reversal: 'entryStatusWaitForReversal',
  in_buy_zone: 'entryStatusInBuyZone',
  holding: 'entryStatusHolding',
  extended: 'entryStatusExtended',
  below_stop: 'entryStatusBelowStop',
  unknown: 'entryStatusUnknown',
};

/**
 * Resolve a raw backend code (snake_case string, possibly undefined/null) to
 * its display descriptor. Anything unrecognized degrades to `unknown` rather
 * than throwing - mirrors the backend classifier's graceful-degrade contract.
 */
export function resolveEntryStatus(code: string | null | undefined): EntryStatusDisplay {
  if (code && Object.prototype.hasOwnProperty.call(MAP, code)) {
    return MAP[code as EntryStatusCode];
  }
  return MAP.unknown;
}

/** True when the code is a real (non-unknown) status worth rendering. */
export function isKnownEntryStatus(code: string | null | undefined): boolean {
  return resolveEntryStatus(code).code !== 'unknown';
}

/**
 * Price-band advice for statuses that are not an immediate buy action. Uses the
 * same entry-to-entry*(1+band) range as the buy-zone classifier and returns null
 * when the status or entry anchor cannot support a useful range.
 */
export function resolveBuyZoneAdvice(
  code: string | null | undefined,
  entryPrice: number | null | undefined,
  buyBandPct: number = 0.03,
): BuyZoneAdvice | null {
  const status = resolveEntryStatus(code).code;
  const entry = typeof entryPrice === 'number' ? entryPrice : Number(entryPrice);
  const band = Number.isFinite(buyBandPct) && buyBandPct >= 0 ? buyBandPct : 0.03;

  if (!BUY_ZONE_ADVICE_CODES.has(status) || !Number.isFinite(entry) || entry <= 0) {
    return null;
  }

  const high = entry * (1 + band);
  return high > entry ? { low: entry, high } : null;
}

/**
 * Live-divergence decision (PLAN_ENTRY_STATUS_PARITY_2026-06-22). The canonical
 * `entry_status` is the SCREENED grade; `live_entry_status` is the coarse LIVE
 * membership. They diverge ONLY when a graded buy code was kept through an
 * intraday drift (e.g. screened Buy, live Holding). Shared by every surface that
 * renders the "screened X, live Y" hint (table, EntryStatusBadge) so the
 * decision can't drift between them.
 *
 * @returns `{ show, liveDisplay }` — `show` true when a divergent KNOWN live code
 *   exists; `liveDisplay` is the resolved descriptor for that live code (else null).
 */
export function resolveLiveDivergence(
  code: string | null | undefined,
  liveCode: string | null | undefined,
): { show: boolean; liveDisplay: EntryStatusDisplay | null } {
  // Only a live status that has LEFT the screened buy zone is a meaningful
  // divergence to surface (holding / extended / below_stop). The backend stamps
  // live_entry_status='in_buy_zone' on EVERY still-in-zone row — including graded
  // buy/accumulate/wait_for_reversal, which are themselves in-zone states — so
  // 'in_buy_zone' must NOT count as a divergence, else the hint would fire on the
  // common agree-case (screened Buy, live still in zone). The intended hint is the
  // drift case: screened Buy, live Holding.
  const show =
    isKnownEntryStatus(liveCode) &&
    liveCode !== code &&
    liveCode !== 'in_buy_zone';
  return { show, liveDisplay: show ? resolveEntryStatus(liveCode) : null };
}

/**
 * Resolve the entry-status code the buy-action gate (FE button enable/disable AND
 * the backend create-guard) should key on. Mirrors backend
 * `_reconcile_buy_action_status`: a live coarse code that has LEFT the buy zone
 * (anything other than `in_buy_zone`) wins, so a name whose live price drifted out
 * (`holding`/`extended`/`below_stop`/`unknown`) is gated out; otherwise the GRADED
 * `entryStatus` wins, so an already `buy`/`strong_buy` name is detected as
 * actionable rather than collapsed to `in_buy_zone`. Keeps the FE button and the
 * backend 409 in agreement.
 */
export function resolveBuyActionGateStatus(
  entryStatus: string | null | undefined,
  liveEntryStatus: string | null | undefined,
): string | null | undefined {
  if (liveEntryStatus && liveEntryStatus !== 'in_buy_zone') return liveEntryStatus;
  return entryStatus;
}

/**
 * Build the live-divergence tooltip suffix shared by every surface (table,
 * badge, chart pill, mobile card) so the `<base> · <prefix>: <live>` format
 * (separator + colon) can't drift between them. `livePrefix` is i18n'd by the
 * caller (table/badge pass the translated "Live"; chart pill/mobile pass the
 * English literal for Pine parity); `liveLabel` is the resolved live-status
 * label. Pure string join.
 */
export function buildLiveDivergenceTooltip(
  base: string,
  livePrefix: string,
  liveLabel: string,
): string {
  return `${base} · ${livePrefix}: ${liveLabel}`;
}
